import React, {useMemo} from 'react'

import Task from "../../model2/task/task"
import FieldFormElement from "../../fields3/render/form-elemnent"
import FormFieldError from "../../gd-form/common/form-error/form-field-error"
import FormModel from "../../gd-form/common/form-model-manager"
import FormFlex from "../../gd-form/form/form-flex/form"
import FormRowFlex from "../../gd-form/form/form-flex/row"
import ButtonSubmit from "../../gd-form/common/btn-submit/btn-submit-ui5-new"
import VScroll from "../../gd-components/scrolling/custom-v-scroll/v-scroll"
import ModalV5 from "../../../app/ui/modal/v5/v5"
import ModalV5_Header from "../../../app/ui/modal/v5/header/header"
import {useForceUpdate} from "../../gd/utils/dom-and-react/react-utils"
import HelpBlock, {HelpType} from "../../gd-components/help-and-hint/help-block/help-block"
import ApiTaskTypes from "../../gd/api/task-types"
import SaveAsTemplateModel, {CREATE_NEW_TASK_TYPE} from "./save-as-template-model"
import {OPTION_GROUPS} from "./save-as-template-options"
import SaveAsTemplate_NewTaskTypeFields from "./new-task-type-fields"
import createTaskType from "./actions/create-task-type"
import FieldTaskType from "../../fields3/system/task/task-type/task-type";
import {FieldType} from "../../fields3/field-type";
import {PopoverMenuItemType} from "../../gd-components/popover-menu/popover-menu/const";

export {SaveAsTemplateField} from "./save-as-template-options"

type Props = {
    task: Task
}

export default function ModalSaveAsTemplate(props: Props) {

    const forceUpdate   = useForceUpdate()
    const { task }      = props
    const model         = useMemo(() => new SaveAsTemplateModel(task), [task])

    const taskTypeField = new FieldTaskType({
        fieldId : gd.fields.task.taskTypeId.fieldId,
        label   : gd.fields.task.taskTypeId.label,
        type    : FieldType.TASK_TYPE,
        classes : {
            PopoverMenu : 'min-w-200'
        }
    })

    taskTypeField.options = [
        { value : CREATE_NEW_TASK_TYPE, label: 'Create new', icon: 'i5-plus fs-12' },
        { type  : PopoverMenuItemType.SEPARATOR },
        ...(taskTypeField.options || []),
    ]

    const onSubmit = async (m: SaveAsTemplateModel) => {

        try {
            // A type that does not exist yet is created with the template already in it
            if (m.isCreateNew) {
                await createTaskType(m, task)
            } else {
                await ApiTaskTypes.saveTaskAsTemplate(m.taskTypeId, task.id, m.selectedFields, m.selectedCustomFieldIds).promise()
            }

            gd.modal.hide()

        } catch (error) {
            gd.apiError(error)
            form.resetSubmit()
        }
    }

    // Kept for the life of the modal, not rebuilt per render: FormFieldError and ButtonSubmit
    // register themselves with the form they were mounted with, so a new one on every render is a
    // form whose error components and submit button are the ones of the first render - the required
    // task type name would block the submit without ever showing why.
    const form = useMemo(() => {
        const formModel = new FormModel(model, { onSubmit })

        // there is nothing to change before submitting - the modal opens on a valid selection
        formModel.isFormChanged = true

        return formModel
    }, [model])

    // When the task type changes via the dropdown, re-derive the custom field
    // selection from the new task type's available fields so the checkboxes
    // re-render and the user never sees fields that won't be saved.
    // "Create new" is not a task type: the content stays the one of the task type it was picked
    // from, so only a real selection moves sourceTaskTypeId along.
    form.onFieldChanged(taskTypeField.fieldId, (newValue: ID) => {

        if (newValue !== CREATE_NEW_TASK_TYPE) model.sourceTaskTypeId = newValue

        model.selectedCustomFieldIds = model.customFieldOptions.map(o => o.id)
        forceUpdate()
    })

    const onFieldToggle = (id: ID) => {
        model.selectedFields = gd.utils.toggleArrayValue(model.selectedFields, id)
        forceUpdate()
    }

    const onCustomFieldToggle = (id: ID) => {
        model.selectedCustomFieldIds = gd.utils.toggleArrayValue(model.selectedCustomFieldIds, id)
        forceUpdate()
    }

    const labelWidth = 130
    const labelClass = "ui5-txt-not-important m-r-45 h-36 flex-row-left-center p-l-45"

    return (
        <ModalV5 className="modal-save-as-template w-650 min-h-500 h-auto">
            <ModalV5_Header title="Save as template" underline={false}/>


                <FormFlex className="p-h-30 p-t-10 p-b-10">

                    {!model.isCreateNew && (
                        <HelpBlock type={HelpType.IMPORTANT} className="m-b-25">
                            Saving will override the existing template content for the selected task type.
                            Please note: only custom fields added to the task type can be saved in a template.
                        </HelpBlock>
                    )}

                    <VScroll
                        className       = "max-h-500"
                        containerClass  = "max-h-500"
                    >

                        <FormRowFlex
                            className   = "m-b-25"
                            label       = {taskTypeField.label}
                            width       = {labelWidth}
                            labelClass  = {labelClass}
                        >
                        <div className="flex-column-left-top">
                            <FieldFormElement className     = "w-300"
                                              field         = {taskTypeField}
                                              form          = {form}
                                              model         = {model}
                                              configOptions = {{ showIcon: true }}/>
                            <FormFieldError form={form} fieldId={taskTypeField.fieldId}/>
                        </div>
                    </FormRowFlex>

                    {model.isCreateNew && (
                        <SaveAsTemplate_NewTaskTypeFields form       = {form}
                                                          model      = {model}
                                                          labelWidth = {labelWidth}
                                                          labelClass = {labelClass}/>
                    )}

                    {OPTION_GROUPS.map(group => (
                        <FormRowFlex key        = {group.title}
                                     label      = {group.title}
                                     width      = {labelWidth}
                                     labelClass = {labelClass}>
                            <div className="flex-column-left-top p-t-10">
                                {group.items.map(o => {
                                    const isSelected = model.selectedFields.includes(o.id)
                                    return (
                                        <div key       = {o.id}
                                             className = "flex-row-left-center m-b-15 cursor-pointer"
                                             onClick   = {() => onFieldToggle(o.id)}>
                                            <div className={Classes('ui5-cb s-16', {selected: isSelected})}/>
                                            <div className="m-l-8 ui5-txt">{o.title}</div>
                                        </div>
                                    )
                                })}
                            </div>
                        </FormRowFlex>
                    ))}

                    {!!model.customFieldOptions.length && (
                        <FormRowFlex label      = "Custom fields"
                                     width      = {labelWidth}
                                     labelClass = {labelClass}>
                            <div className="flex-column-left-top p-t-10">
                                {model.customFieldOptions.map(o => {
                                    const isSelected = model.selectedCustomFieldIds.includes(o.id)
                                    return (
                                        <div key       = {o.id}
                                             className = "flex-row-left-center m-b-15 cursor-pointer"
                                             onClick   = {() => onCustomFieldToggle(o.id)}>
                                            <div className={Classes('ui5-cb s-16', {selected: isSelected})}/>
                                            <div className="m-l-8 ui5-txt">{o.title}</div>
                                        </div>
                                    )
                                })}
                            </div>
                        </FormRowFlex>
                    )}
                    </VScroll>
                </FormFlex>



            <div className="flex-row-center-center flex-no-shrink h-90">
                <ButtonSubmit form      = {form}
                              className = "min-w-150 p-h-8"
                              label     = "Save"/>
            </div>
        </ModalV5>
    )
}
